import { motion, useScroll, useTransform } from "framer-motion";
import { useCallback, useRef, useState } from "react";
import { ORDER_ONLINE_URL, DELIVERY_URL } from "../lib/data";
import { MenuThumb, MenuLightbox } from "../components/MenuLightbox";
import Reviews from "../components/Reviews";
import HowToOrder from "../components/HowToOrder";
import ContactBar from "../components/ContactBar";

const BURGER_PAGES = [
    {
        src: "/menus/burger-menu-1.webp",
        label: "Burgers & Sides",
        alt: "The Gaffer menu — beef, chicken and veggie burgers, sides, sauces and extras",
        testId: "burger-menu-page-1",
        w: 1170,
        h: 822,
    },
    {
        src: "/menus/burger-menu-2.webp",
        label: "Kids, Shakes & Coffee",
        alt: "The Gaffer menu — kids meals, thick shakes, OMGaffer shakes, soft drinks, coffee and soft serve",
        testId: "burger-menu-page-2",
        w: 1170,
        h: 818,
    },
];

const DRINKS_PAGES = [
    {
        src: "/menus/drinks-menu-1.webp",
        w: 1268,
        h: 2690,
        label: "On Tap, Beer & Spirits",
        alt: "The Gaffer drinks menu — on tap, beer, spirits and ginger beer",
        testId: "drinks-menu-page-1",
    },
    {
        src: "/menus/drinks-menu-2.webp",
        w: 1268,
        h: 2690,
        label: "RTDs, Cocktails & Wine",
        alt: "The Gaffer drinks menu — ready to drink, cocktails, wine and sparkling",
        testId: "drinks-menu-page-2",
    },
];

export default function MenuPage() {
    const [openPage, setOpenPage] = useState(null);
    const close = useCallback(() => setOpenPage(null), []);
    return (
        <>
            <section
                data-testid="menu-page-section"
                className="relative pt-32 md:pt-36 pb-12 md:pb-16"
            >
                <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14 text-center">
                    <p className="overline mb-4">The Menu</p>
                    <h1 className="font-display uppercase text-[#121212] text-[clamp(2.6rem,6vw,5rem)] leading-[0.92]">
                        What&rsquo;s on
                        <br />
                        the <span className="text-[#EB4A18]">menu.</span>
                    </h1>
                    <p className="mt-6 text-[#4A4A4A] text-base md:text-lg max-w-xl mx-auto leading-relaxed">
                        Fresh Burgers, Sides and Shakes.
                    </p>
                </div>
            </section>

            <section
                data-testid="menu-image-section"
                className="relative pb-12 md:pb-16"
            >
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.05 }}
                        transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
                        data-testid="burger-menu-section"
                        className="max-w-[1100px] mx-auto space-y-10 md:space-y-14"
                    >
                        {BURGER_PAGES.map((pg) => (
                            <div key={pg.testId}>
                                <p className="overline text-center mb-3">{pg.label}</p>
                                <MenuThumb page={pg} onOpen={setOpenPage} />
                            </div>
                        ))}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.05 }}
                        transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
                        data-testid="drinks-menu-section"
                        className="mt-16 md:mt-24"
                    >
                        <p className="overline text-center mb-6 text-[#EB4A18]">Drinks</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-8 items-start">
                            {DRINKS_PAGES.map((pg) => (
                                <div key={pg.testId}>
                                    <p className="overline text-center mb-3">{pg.label}</p>
                                    <MenuThumb page={pg} onOpen={setOpenPage} className="max-w-[520px]" />
                                </div>
                            ))}
                        </div>
                    </motion.div>
                    <p className="mt-6 text-center overline text-[#6B6B6B]">
                        Prices in AUD · GST included
                    </p>

                    <div className="mt-14 md:mt-20 text-center">
                        <div className="flex flex-wrap items-center justify-center gap-4">
                            <a
                                href={ORDER_ONLINE_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="menu-page-order-online-btn"
                                className="btn-primary text-base px-10 py-5"
                            >
                                Order Pickup
                                <span aria-hidden="true">→</span>
                            </a>
                            <a
                                href={DELIVERY_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="menu-page-delivery-btn"
                                className="btn-secondary text-base px-10 py-5"
                            >
                                Get Delivery
                                <span aria-hidden="true">→</span>
                            </a>
                        </div>
                        <p className="mt-5 overline text-[#6B6B6B]">
                            Pickup via Chewzie · Delivery via Uber Eats
                        </p>
                    </div>
                </div>
            </section>

            <ArrowWave />

            <HowToOrder />
            <Reviews />
            <ContactBar />
            <MenuLightbox page={openPage} onClose={close} />
        </>
    );
}

// Desktop-only: a centered SVG burger that BUILDS layer-by-layer as
// the user scrolls toward the How To Order section. Bottom bun lands
// first, then patty, cheese, lettuce, tomato, finally top bun with
// sesame seeds. Pure scroll-driven (no autoplay) so it feels tied to
// the user's intent.
function ArrowWave() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 95%", "end 40%"],
    });

    return (
        <div
            ref={ref}
            aria-hidden="true"
            className="flex relative w-full justify-center items-end px-6 md:px-10 lg:px-14 py-10 md:py-12 lg:py-16 bg-[#FAF8F4]"
        >
            <BuildingBurger scrollYProgress={scrollYProgress} />
        </div>
    );
}

function BurgerLayer({ scrollYProgress, start, end, drop = 36, children }) {
    const opacity = useTransform(scrollYProgress, [start, end], [0, 1]);
    const y = useTransform(scrollYProgress, [start, end], [-drop, 0]);
    return (
        <motion.g style={{ opacity, y }}>
            {children}
        </motion.g>
    );
}

function BuildingBurger({ scrollYProgress }) {
    // SVG viewBox: 200 x 180 (wider than tall, burger sits low)
    return (
        <svg
            viewBox="0 0 200 180"
            className="w-[150px] md:w-[180px] lg:w-[220px] h-auto overflow-visible"
        >
            {/* Plate shadow */}
            <motion.ellipse
                cx="100"
                cy="170"
                rx="86"
                ry="6"
                fill="rgba(0,0,0,0.12)"
                style={{
                    opacity: useTransform(scrollYProgress, [0, 0.1], [0, 1]),
                }}
            />

            {/* 1. Bottom bun (lands first) */}
            <BurgerLayer scrollYProgress={scrollYProgress} start={0.0} end={0.13}>
                <path
                    d="M16 150 Q100 184 184 150 L184 140 Q100 168 16 140 Z"
                    fill="#C77A3A"
                />
                <path
                    d="M16 142 Q100 168 184 142 L184 138 Q100 162 16 138 Z"
                    fill="#A85F23"
                    opacity="0.6"
                />
            </BurgerLayer>

            {/* 2. Patty (smashed dark brown) */}
            <BurgerLayer scrollYProgress={scrollYProgress} start={0.11} end={0.24}>
                <rect
                    x="14"
                    y="118"
                    width="172"
                    height="22"
                    rx="6"
                    fill="#3E2418"
                />
                <rect
                    x="14"
                    y="118"
                    width="172"
                    height="5"
                    rx="2"
                    fill="#5A3422"
                />
            </BurgerLayer>

            {/* 3. Cheese (yellow, slightly drooping corners) */}
            <BurgerLayer scrollYProgress={scrollYProgress} start={0.22} end={0.35}>
                <path
                    d="M8 108 L192 108 L186 124 L180 118 L170 126 L160 118 L150 126 L140 118 L130 126 L120 118 L110 126 L100 118 L90 126 L80 118 L70 126 L60 118 L50 126 L40 118 L30 126 L20 118 L14 124 Z"
                    fill="#F2B438"
                />
            </BurgerLayer>

            {/* 4. Lettuce (green wavy frill) */}
            <BurgerLayer scrollYProgress={scrollYProgress} start={0.33} end={0.46}>
                <path
                    d="M6 98 Q22 86 38 98 Q54 86 70 98 Q86 86 102 98 Q118 86 134 98 Q150 86 166 98 Q182 86 194 98 L188 110 Q160 100 132 110 Q104 100 76 110 Q48 100 22 110 Z"
                    fill="#2F8F4E"
                />
                <path
                    d="M16 100 Q40 92 64 100 Q88 92 112 100 Q136 92 160 100 Q180 92 188 100"
                    stroke="#3FA862"
                    strokeWidth="1.5"
                    fill="none"
                />
            </BurgerLayer>

            {/* 5. Tomato slice */}
            <BurgerLayer scrollYProgress={scrollYProgress} start={0.43} end={0.55}>
                <path
                    d="M18 88 Q100 70 182 88 L182 96 Q100 78 18 96 Z"
                    fill="#C7301F"
                />
                <ellipse cx="60" cy="86" rx="5" ry="2.5" fill="#F4B5A6" />
                <ellipse cx="140" cy="86" rx="5" ry="2.5" fill="#F4B5A6" />
            </BurgerLayer>

            {/* 6. Top bun (final landing — sits with a gap above the burger) */}
            <BurgerLayer
                scrollYProgress={scrollYProgress}
                start={0.52}
                end={0.68}
                drop={70}
            >
                {/* Main dome — proper filled dome, hovering above the tomato */}
                <path
                    d="M14 68 Q100 8 186 68 Z"
                    fill="#E69248"
                />
                {/* Bottom edge highlight band (subtle shadow under crust) */}
                <path
                    d="M14 68 Q100 54 186 68 L186 70 Q100 56 14 70 Z"
                    fill="#A85F23"
                    opacity="0.45"
                />
                {/* Inner highlight stripe on the crown */}
                <path
                    d="M52 36 Q100 16 148 36"
                    fill="none"
                    stroke="#F4B070"
                    strokeWidth="3"
                    strokeLinecap="round"
                    opacity="0.6"
                />
                {/* Sesame seeds — repositioned across the dome */}
                <g fill="#FFF1D0">
                    <ellipse cx="65" cy="38" rx="3" ry="1.6" transform="rotate(-22 65 38)" />
                    <ellipse cx="100" cy="24" rx="3" ry="1.6" />
                    <ellipse cx="135" cy="38" rx="3" ry="1.6" transform="rotate(22 135 38)" />
                    <ellipse cx="48" cy="50" rx="3" ry="1.6" transform="rotate(-30 48 50)" />
                    <ellipse cx="152" cy="50" rx="3" ry="1.6" transform="rotate(30 152 50)" />
                    <ellipse cx="82" cy="44" rx="3" ry="1.6" transform="rotate(-10 82 44)" />
                    <ellipse cx="118" cy="44" rx="3" ry="1.6" transform="rotate(10 118 44)" />
                </g>
            </BurgerLayer>

            {/* Steam wisps appear once the burger is fully built */}
            <motion.g
                style={{
                    opacity: useTransform(
                        scrollYProgress,
                        [0.66, 0.78],
                        [0, 0.6]
                    ),
                }}
                fill="none"
                stroke="#9B9B9B"
                strokeWidth="2"
                strokeLinecap="round"
            >
                <path d="M78 0 Q83 -12 78 -24" />
                <path d="M100 -6 Q105 -18 100 -30" />
                <path d="M122 0 Q127 -12 122 -24" />
            </motion.g>
        </svg>
    );
}
